import classNames from 'classnames';
import type { ComponentProps } from 'react';

import type {
    Card as CardType,
    DataAttributes,
    Intro as IntroType,
    ReferenceCardsBlock,
} from '@/types';
import { Intro } from '@/ui/Intro/Intro';
import { Section } from '@/ui/Section/Section';

import { Card } from './components/Card/Card';
import { ExampleCard } from './components/ExampleCard/ExampleCard';
import { RoundtableCard } from './components/RoundtableCard/RoundtableCard';

import styles from './ReferenceCards.module.scss';

interface Props extends DataAttributes {
    cards?: ReferenceCardsBlock['cards'];
    className?: string;
    collectionClassName?: string;
    itemsClassName?: string;
    hasIntro?: boolean;
    intro?: IntroType;
    introNextToCards?: boolean;
    backgroundColor?: 'white' | 'gray';
    isHero?: boolean;
}

export function ReferenceCards({
    backgroundColor = 'white',
    className,
    collectionClassName,
    itemsClassName,
    cards,
    isHero,
    hasIntro,
    intro,
    introNextToCards,
    ...attributes
}: Props) {
    return (
        <Section
            data-reference-cards
            data-reference-cards-count={cards?.length ?? 0}
            data-reference-cards-is-hero={isHero}
            {...attributes}
            className={className}
            primary={backgroundColor}
            isHero={isHero}
        >
            <div
                className={classNames(styles.collection, collectionClassName, {
                    [styles.introNextToCards]: introNextToCards,
                })}
            >
                {hasIntro && intro && (
                    <Intro
                        {...intro}
                        className={styles.intro}
                        classes={{ title: styles.title, body: styles.body, byline: styles.byline }}
                        isHero={isHero}
                        hasBackground={backgroundColor !== 'white'}
                    />
                )}

                {cards && cards.length > 0 && (
                    <div
                        className={classNames(styles.items, itemsClassName, {
                            [styles.hasIntro]: hasIntro,
                        })}
                    >
                        {cards.map(({ card, hideReadMoreLabel, id }) => {
                            if (!card) {
                                return null;
                            }

                            const props = getCardProps(card, { hideReadMoreLabel });

                            if (card.contentType === 'examples') {
                                return (
                                    <ExampleCard
                                        key={id}
                                        example={card}
                                        slug={card.slug}
                                        {...props}
                                    />
                                );
                            }

                            if (card.contentType === 'round-tables') {
                                return <RoundtableCard key={id} video={card.video} {...props} />;
                            }

                            return <Card key={id} {...props} />;
                        })}
                    </div>
                )}
            </div>
        </Section>
    );
}

function getCardProps(
    card: CardType,
    { hideReadMoreLabel }: { hideReadMoreLabel?: boolean },
): ComponentProps<typeof Card> {
    if (card.contentType === 'casestudies') {
        return {
            path: card.path,
            image: card.media,
            title: card.title,
            readMoreLabel: hideReadMoreLabel ? undefined : 'Read more',
        };
    }

    if (card.contentType === 'examples') {
        return {
            path: card.path,
            image: card.screenshot,
            title: card.title,
            readMoreLabel: hideReadMoreLabel ? undefined : 'See example',
            imageEffects: { scaleCrop: { height: 1200, width: 1920 } },
        };
    }

    if (card.contentType === 'pages') {
        return {
            path: card.path,
            image: card.meta?.image,
            title: card.title ?? card.meta?.title ?? '',
            description: card.meta?.description,
            readMoreLabel: hideReadMoreLabel ? undefined : 'Read more',
        };
    }

    if (card.contentType === 'stories') {
        const category = card.categories?.[0];

        return {
            chipText: card.site === 'changelog' ? 'Changelog' : category?.title,
            path: card.path,
            image: card.meta?.image ?? card.previewImage ?? card.headerImage,
            title: card.title ?? '',
            readMoreLabel: hideReadMoreLabel ? undefined : 'Read article',
        };
    }

    if (card.contentType === 'round-tables') {
        return {
            chipText: `Episode ${card.episodeNumber}`,
            path: card.path,
            image: card.image,
            title: card.title,
            readMoreLabel: hideReadMoreLabel ? undefined : 'More details',
            readMoreUrl: card.path,
        };
    }

    throw new Error(`Card type not handled`);
}
